import { useEffect, useRef, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import Pagination, { usePagination } from '../components/Pagination';

const EMPTY_PRODUCT = {
  name: '', description: '', insurer_name: '', insurer_link: '',
  insurer_scheme_reference: '', eligibility_days: '', information_pack_link: '', email_subject_template: '', email_body_template: '',
};

// README §3.1 — the fixed static whitelist, valid on every email body
// template. employment_effective_date is the enrolment's
// EmployeeEnrolment.effective_date (the employment effective date entered
// on the Addition row), sent as dd/mm/yyyy. The two link tokens are named
// after the words the email shows as the link (to the product's insurer
// link / information pack link). The backend still accepts the old names
// (effective_date, insurer_link, information_pack_link).
const STATIC_TOKENS = ['first_name', 'last_name', 'employer_name', 'click_here_to_join', 'insurer_scheme_reference', 'your_information_pack_here', 'employment_effective_date'];

// A labelled Jinja2 textarea + a row of clickable "{{ token }}" chips for
// every token the save-time whitelist (§3.1) will actually accept —
// clicking one inserts it at the cursor rather than just describing it in
// a hint, since the whitelist is easy to mistype from memory.
function TemplateField({ label, value, onChange, tokens, required, hint }) {
  const ref = useRef(null);
  // "Expand" pops the editor out into a near-full-screen panel for longer
  // templates; Esc or "Done" closes it again.
  const [expanded, setExpanded] = useState(false);

  // Grow the textarea to fit its content (the inline editor only — the
  // expanded one already fills the screen), so a long template isn't
  // stuck scrolling inside a few-line box.
  useEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight + 2, 240), 600)}px`;
  }, [value, expanded]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  function insert(token) {
    const snippet = `{{ ${token} }}`;
    const el = ref.current;
    if (!el) { onChange((value || '') + snippet); return; }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + snippet + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  }

  return (
    <div
      className="field"
      style={expanded ? {
        position: 'fixed', inset: '4vh 4vw', zIndex: 1000, margin: 0, padding: 16, display: 'flex', flexDirection: 'column',
        background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 8, boxShadow: '0 0 0 100vmax rgba(0,0,0,.35)',
      } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <label style={{ margin: 0 }}>{label} {required && <span className="req">*</span>}</label>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn sm" onClick={() => setExpanded((x) => !x)}>{expanded ? 'Done' : 'Expand editor'}</button>
      </div>
      <textarea
        ref={ref}
        required={required}
        rows={10}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={expanded
          ? { flex: 1, height: 'auto', resize: 'none', lineHeight: 1.5 }
          : { resize: 'vertical', minHeight: 240, lineHeight: 1.5 }}
      />
      <div className="hint" style={{ marginBottom: 4 }}>Available tokens — click to insert:</div>
      <div className="btn-row" style={{ marginBottom: 4 }}>
        {tokens.map((t) => (
          <button type="button" key={t} className="chip" onClick={() => insert(t)}>{`{{ ${t} }}`}</button>
        ))}
      </div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

const defaultSubject = (name) => `Your ${name || '{product name}'} scheme invitation`;

/* ICIB-11 — Manage group products.
   There is no more shared master Product catalog (§2.9 removed) — every
   `GroupProduct` is created fully self-contained, directly under one
   group (§2.10): name, insurer info, its own insurer-side scheme
   reference, eligibility window, and its own branded invitation content,
   all inline, none of it falling back to a catalog default any more.
   This view is entirely group-scoped as a result — there's nothing to
   show without a group selected. */
function ProductsView() {
  const {
    db, status, groupId, curGroup, product, gProducts, createGroupProduct, updateGroupProduct, detachProduct, reactivateGroupProduct,
  } = useIcibAdmin();
  const { openDrawer, closeDrawer } = useDrawer();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [busy, setBusy] = useState(false);

  // gProducts includes removed rows too (ICIB-11f fetched with
  // ?include_inactive=true — see IcibAdminContext's refresh()), each
  // carrying is_active, so a removed row's "Reactivate" action (surfaced
  // on its own drawer below) is discoverable straight from this list.
  const attached = groupId ? gProducts(groupId) : [];
  const attachedPage = usePagination(attached);

  async function submitProduct(e) {
    e.preventDefault();
    if (!groupId) return;
    setBusy(true);
    const res = await createGroupProduct(groupId, form);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not create this product.', 'bad', res.error); return; }
    toast(`${res.name} added to ${curGroup()?.name || 'this company'}.`, 'ok');
    setForm(EMPTY_PRODUCT);
  }

  // ICIB-11n — undoes onDetach below, for a row the list already shows
  // (tagged 'removed') rather than one only discoverable by trying to
  // create a duplicate.
  async function onReactivateGroupProduct(gp) {
    const res = await reactivateGroupProduct(groupId, gp.product_id);
    if (res?.error) toast(res.message || 'Could not reactivate this product.', 'bad', res.error);
    else { toast(`${product(gp.product_id)?.name || gp.product_id} reactivated.`, 'ok'); closeDrawer(); }
  }

  async function onDetach(gp) {
    if (!window.confirm(`Remove ${product(gp.product_id)?.name || gp.product_id} from this company?`)) return;
    const res = await detachProduct(groupId, gp.product_id);
    if (res?.error) toast(res.message || 'Could not remove this product.', 'bad', res.error);
    else { toast('Removed from this company.', 'ok'); closeDrawer(); }
  }

  function openGroupProduct(gp) {
    openDrawer(product(gp.product_id)?.name || gp.product_id, `ICIB-11 · ${curGroup()?.company_code}`, (
      <GroupProductDrawer gid={groupId} gp={gp} onSave={updateGroupProduct} onDetach={onDetach} onReactivate={onReactivateGroupProduct} />
    ));
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Products</div>
      <div className="page-h"><h2>Subsidised product level set up</h2></div>
      <p className="page-sub">
        {groupId
          ? `Which products ${curGroup()?.name || 'this company'} offers — created directly under this company, no shared catalog.`
          : 'Select a company above — every product belongs to one specific company, there is no shared catalog to browse here.'}
      </p>

      {!groupId ? (
        <div className="card"><div className="empty"><b>Select a company</b>Choose a company from the scope selector above.</div></div>
      ) : (
        <>
          {db.errors?.groupProducts && (
            <div className="note bad" style={{ marginBottom: 10 }}>
              <b>Couldn't load this company's products</b>{db.errors.groupProducts}
            </div>
          )}
          <div className="grid g2">
            <div>
              <div className="sec-h" style={{ marginTop: 0 }}><h3>Company subsidised product</h3><span className="spacer" /><span className="ep">GET /companies/{'{company_id}'}/products?include_inactive=true</span></div>
              <div className="card">
                {attached.length ? (
                  <div className="tbl-wrap">
                    <table>
                      <thead><tr><th>Product</th><th>Insurer scheme ref</th><th>Eligibility</th><th>Status</th><th></th></tr></thead>
                      <tbody>
                        {attachedPage.pageItems.map((gp) => (
                          <tr className="clickable" key={gp.product_id} onClick={() => openGroupProduct(gp)}>
                            <td className="strong">{gp.name || gp.product_id}</td>
                            <td className="mono">{gp.insurer_scheme_reference}</td>
                            <td className="mono">{gp.eligibility_days} day{gp.eligibility_days === 1 ? '' : 's'}</td>
                            <td><span className={'tag' + (gp.is_active === false ? ' off' : ' ok')}>{gp.is_active === false ? 'removed' : 'active'}</span></td>
                            <td><button className="btn sm" onClick={(e) => { e.stopPropagation(); openGroupProduct(gp); }}>Open</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="empty"><b>No products yet</b>Add the first one on the right — required before it can appear on this company's intake form.</div>}
                <Pagination {...attachedPage} />
              </div>
            </div>

            <div>
              <div className="sec-h" style={{ marginTop: 0 }}><h3>New product</h3><span className="spacer" /><span className="ep">POST /companies/{'{company_id}'}/products</span></div>
              <div className="card card-b">
                <form onSubmit={submitProduct}>
                  <div className="field"><label>Name <span className="req">*</span></label>
                    <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="UltraCare" /></div>
                  <div className="field"><label>Description</label>
                    <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
                  <div className="field"><label>Insurer name <span className="req">*</span></label>
                    <input required value={form.insurer_name} onChange={(e) => setForm((f) => ({ ...f, insurer_name: e.target.value }))} placeholder="Southern Cross" /></div>
                  <div className="field"><label>Insurer link <span className="req">*</span></label>
                    <input required type="url" value={form.insurer_link} onChange={(e) => setForm((f) => ({ ...f, insurer_link: e.target.value }))} /></div>
                  <div className="field"><label>Insurer scheme reference <span className="req">*</span></label>
                    <input required value={form.insurer_scheme_reference} onChange={(e) => setForm((f) => ({ ...f, insurer_scheme_reference: e.target.value }))} placeholder="GGE0001FSA" />
                    <div className="hint">The insurer's own reference code for this company's enrolment in this product.</div></div>
                  <div className="field"><label>Eligibility (days) <span className="req">*</span></label>
                    <input required type="number" min="0" step="1" value={form.eligibility_days} onChange={(e) => setForm((f) => ({ ...f, eligibility_days: e.target.value }))} placeholder="0" />
                    <div className="hint">How many days an employee must be employed before this product's cover applies — a new addition is only enrolled (and invited) once their employment effective date plus this many days has passed, and waits in Pending enrolment until then.</div></div>
                  <div className="field"><label>Information pack link <span className="req">*</span></label>
                    <input required type="url" value={form.information_pack_link} onChange={(e) => setForm((f) => ({ ...f, information_pack_link: e.target.value }))} /></div>
                  <div className="field"><label>Email subject line</label>
                    <input maxLength={255} value={form.email_subject_template} onChange={(e) => setForm((f) => ({ ...f, email_subject_template: e.target.value }))} placeholder={defaultSubject(form.name)} />
                    <div className="hint">Optional. Leave blank to use the default.</div></div>
                  <TemplateField
                    label="Email body template" required
                    value={form.email_body_template}
                    onChange={(v) => setForm((f) => ({ ...f, email_body_template: v }))}
                    tokens={STATIC_TOKENS}
                    hint="No company-specific form_data field is ever a valid merge token — only the static set above."
                  />
                  <div className="btn-row"><button type="submit" className="btn pri" disabled={busy}>{busy ? 'Adding…' : 'Add product'}</button></div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// Every field on a GroupProduct is required and self-contained now (no
// catalog default to fall back to, §2.9/§2.10) — this drawer edits all of
// them, not just the insurer-scheme-reference/eligibility/link/template
// slice it used to when those were the only per-group overrides on top of
// a shared catalog entry.
function GroupProductDrawer({ gid, gp, onSave, onDetach, onReactivate }) {
  const { availableTokens, loadAvailableTokens } = useIcibAdmin();
  const toast = useToast();
  const [form, setForm] = useState({
    name: gp.name || '', description: gp.description || '', insurer_name: gp.insurer_name || '', insurer_link: gp.insurer_link || '',
    insurer_scheme_reference: gp.insurer_scheme_reference || '', eligibility_days: gp.eligibility_days ?? '',
    information_pack_link: gp.information_pack_link || '', email_subject_template: gp.email_subject_template || '',
    email_body_template: gp.email_body_template || '',
  });
  const [busy, setBusy] = useState(false);
  const [reactivateBusy, setReactivateBusy] = useState(false);
  const removed = gp.is_active === false;

  // ICIB-11l — GET /companies/{gid}/available-tokens. Updated: `form_fields`
  // is gone — no `Employee.form_data` field is a valid merge token any
  // more, so this always returns the same fixed `static` set STATIC_TOKENS
  // already has locally. Still fetched (rather than just using
  // STATIC_TOKENS directly) so a future change to the server-side
  // whitelist shows up here without a frontend redeploy.
  useEffect(() => { loadAvailableTokens(gid); }, [gid]); // eslint-disable-line react-hooks/exhaustive-deps
  const tokensRes = availableTokens[gid];
  const groupTokens = tokensRes?.static || STATIC_TOKENS;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await onSave(gid, gp.product_id, form);
    setBusy(false);
    if (res?.error) toast(res.message || 'Could not save this product.', 'bad', res.error);
    else toast('Saved.', 'ok');
  }

  async function reactivate() {
    setReactivateBusy(true);
    await onReactivate(gp);
    setReactivateBusy(false);
  }

  return (
    <form onSubmit={submit}>
      {removed && <div className="note" style={{ marginBottom: 12 }}><b>Removed from this company</b>Its saved settings below are kept — reactivate to bring it back rather than editing while removed.</div>}
      <fieldset disabled={removed} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="field"><label>Name</label><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
        <div className="field"><label>Description</label><input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
        <div className="field"><label>Insurer name</label><input value={form.insurer_name} onChange={(e) => setForm((f) => ({ ...f, insurer_name: e.target.value }))} /></div>
        <div className="field"><label>Insurer link</label><input type="url" value={form.insurer_link} onChange={(e) => setForm((f) => ({ ...f, insurer_link: e.target.value }))} /></div>
        <div className="field"><label>Insurer scheme reference</label>
          <input value={form.insurer_scheme_reference} onChange={(e) => setForm((f) => ({ ...f, insurer_scheme_reference: e.target.value }))} />
          <div className="hint">Cannot be cleared once set — only replaced with a different value (§2.10).</div></div>
        <div className="field"><label>Eligibility (days)</label>
          <input type="number" min="0" step="1" value={form.eligibility_days} onChange={(e) => setForm((f) => ({ ...f, eligibility_days: e.target.value }))} />
          <div className="hint">Cannot be cleared once set — only replaced with a different non-negative integer (§2.10). A change only affects additions submitted afterwards — ones already waiting in Pending enrolment keep the date they were given (§2.6a).</div></div>
        <div className="field"><label>Information pack link</label><input type="url" value={form.information_pack_link} onChange={(e) => setForm((f) => ({ ...f, information_pack_link: e.target.value }))} /></div>
        <div className="field"><label>Email subject line</label><input maxLength={255} value={form.email_subject_template} onChange={(e) => setForm((f) => ({ ...f, email_subject_template: e.target.value }))} placeholder={defaultSubject(form.name)} /></div>
        <TemplateField
          label="Email body template"
          value={form.email_body_template}
          onChange={(v) => setForm((f) => ({ ...f, email_body_template: v }))}
          tokens={groupTokens}
          hint="Cannot be cleared once set — every field here is required, no catalog default to fall back to."
        />
      </fieldset>
      <div className="btn-row">
        {!removed && <button type="submit" className="btn pri sm" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>}
        {!removed
          ? <button type="button" className="btn danger sm" onClick={() => onDetach(gp)}>Remove from company</button>
          : <button type="button" className="btn sm" disabled={reactivateBusy} onClick={reactivate}>{reactivateBusy ? 'Reactivating…' : 'Reactivate for this company'}</button>}
      </div>
    </form>
  );
}

export default ProductsView;
